import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideChevronsUpDown, lucideLogOut } from '@ng-icons/lucide';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSidebarMenu, HlmSidebarMenuButton, HlmSidebarMenuItem } from '@spartan-ng/helm/sidebar';
import { concatMap } from 'rxjs';
import { CompanyResource } from '../../api-users/model/companyResource';
import { UserCompanyService } from '../../api-users/api/userCompany.service';
import { UserCompanyStatusRequest } from '../../api-users/model/userCompanyStatusRequest';
import { UserResource } from '../../api-users/model/userResource';
import { UsersService } from '../../api/api/users.service';
import { AuthMyService } from '../../auth/auth.service';
import { CompanyThemeService } from '../../utils/company-theme/company-theme.service';

@Component({
    selector: 'app-user-menu',
    templateUrl: './app-user-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HlmAvatarImports, HlmDropdownMenuImports, HlmSidebarMenu, HlmSidebarMenuButton, HlmSidebarMenuItem, NgIcon],
    providers: [provideIcons({ lucideCheck, lucideChevronsUpDown, lucideLogOut })],
})
export class AppUserMenuComponent {
    public readonly user = input<UserResource | null>(null);

    protected readonly fullName = computed(() => {
        const user = this.user();
        return user ? `${user.firstname} ${user.lastname}`.trim() : 'Uživatel';
    });
    protected readonly initials = computed(() => {
        const user = this.user();
        const initials = `${user?.firstname?.charAt(0) ?? ''}${user?.lastname?.charAt(0) ?? ''}`.trim();
        return initials || '?';
    });
    protected readonly companies = computed(() => this.user()?.companies ?? []);

    private readonly router = inject(Router);
    private readonly authService = inject(AuthMyService);
    private readonly themeService = inject(CompanyThemeService);
    private readonly userCompanyService = inject(UserCompanyService);
    private readonly usersService = inject(UsersService);

    protected isActiveCompany(company: CompanyResource): boolean {
        return this.user()?.activeCompany?.id === company.id;
    }

    protected switchCompany(company: CompanyResource): void {
        const user = this.user();
        if (!user?.id || this.isActiveCompany(company)) {
            return;
        }

        const request: UserCompanyStatusRequest = { companyId: company.id };
        this.userCompanyService
            .setActiveUserCompany(user.id, request)
            .pipe(concatMap(() => this.usersService.deleteUserMeContextCache()))
            .subscribe({
                next: () => {
                    this.themeService.setCompanyThemeImmediately(company);
                    const url = new URL(window.location.href);
                    url.searchParams.set('_reload', Date.now().toString());
                    window.location.href = url.toString();
                },
                error: () => {
                    if (user.activeCompany) {
                        this.themeService.setCompanyThemeImmediately(user.activeCompany);
                    }
                },
            });
    }

    protected signOut(): void {
        void this.router.navigate(['/sign-in'], { replaceUrl: true }).then(navigated => {
            if (navigated) {
                this.authService.signOut().subscribe();
            }
        }).catch(error => console.error('Nepodařilo se přejít na přihlášení.', error));
    }
}
